/**
 * O'Copro - Composant Logo réutilisable
 * 
 * Usage:
 *   import { AppLogo } from '@/components/common/AppLogo';
 *   <AppLogo size="md" />                    // texte navy (fond clair)
 *   <AppLogo size="sm" variant="white" />    // texte blanc (fond coloré/sombre)
 * 
 * Fichiers requis dans src/assets/ :
 *   - ocopro-logo.png        → texte navy (pour fonds clairs)
 *   - ocopro-logo-white.png  → texte blanc (pour fonds colorés/sombres)
 * 
 * Les deux fichiers sont des PNG avec fond transparent.
 * 
 * Variantes:
 *   - variant="default" (défaut) : texte navy — fonds blancs, gris, clairs
 *   - variant="white"            : texte blanc — fonds émeraude, sombres, colorés
 * 
 * Tailles:
 *   - "xs"  : 80px de large  (navbar, badges)
 *   - "sm"  : 120px de large (headers compacts)
 *   - "md"  : 160px de large (dashboard, headers)
 *   - "lg"  : 220px de large (pages d'accueil, login)
 *   - "xl"  : 300px de large (splash screen)
 * 
 * src/components/common/AppLogo.tsx
 */

import React from 'react';

// ✅ Import Vite-compatible — le bundler résout les URLs au build
// Deux versions du logo: texte navy (fond clair) et texte blanc (fond sombre)
import ocoproLogo from '@/assets/ocopro-logo.png';
import ocoproLogoWhite from '@/assets/ocopro-logo-white.png';

// ============================================================================
// TYPES
// ============================================================================

export type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type LogoVariant = 'default' | 'white';

export interface AppLogoProps {
  /** Taille du logo (défaut: 'md') */
  size?: LogoSize;
  /** Variante: 'default' = texte navy (fond clair), 'white' = texte blanc (fond sombre) */
  variant?: LogoVariant;
  /** Classes CSS additionnelles sur le conteneur */
  className?: string;
  /** Callback au clic (optionnel — ex: navigation vers l'accueil) */
  onClick?: () => void;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Largeurs en pixels par taille */
const SIZE_MAP: Record<LogoSize, number> = {
  xs: 80,
  sm: 120,
  md: 160,
  lg: 220,
  xl: 300
};

/** Ratio original après recadrage (~2.43:1) */
const ASPECT_RATIO = 1527 / 629;

// ============================================================================
// COMPOSANT
// ============================================================================

export const AppLogo: React.FC<AppLogoProps> = ({
  size = 'md',
  variant = 'default',
  className = '',
  onClick
}) => {
  const width = SIZE_MAP[size];
  const height = Math.round(width / ASPECT_RATIO);

  // ✅ Sélection du fichier selon la variante — pas de filtre CSS, rendu natif
  const logoSrc = variant === 'white' ? ocoproLogoWhite : ocoproLogo;

  return (
    <div
      className={`inline-flex items-center ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <img
        src={logoSrc}
        alt="O'Copro"
        width={width}
        height={height}
        className="block object-contain"
        style={{ width, height }}
        draggable={false}
      />
    </div>
  );
};

export default AppLogo;
